import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { Divider } from "../../../../components/Divider";
import Spacer from "../../../../components/Spacer";
import { CheckboxGroup } from "../../AIAssistants/components";
import { GuidelinesField } from "../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import { useAiSettingsDefaults } from "../../AIAssistants/hooks";

import { SectionLayout, SettingsFormFooter, SettingsToggle, UnsavedChangesTracker } from "../../Components";

type CoachingReviewsTabProps = {
	setIsUnsafe: () => void;
	setIsSafe: () => void;
};

const ADDITIONAL_GUIDELINES_DESCRIPTION =
	"Add your own rules for Ask PerformYard to check, such as prohibited terminology, required competencies, or rating justification standards. Specific criteria help Ask PerformYard flag issues more accurately.";

const formAuthoringCoachFormSchema = AiSettingsSchema.shape.formAuthoringCoachSettings;

const formAuthoringDetectionCheckboxOptions = [
	{ value: "vagueResponses", label: "Vague Responses", dataTestId: "vague-responses-checkbox" },
	{ value: "speculativeFeedback", label: "Speculative Feedback", dataTestId: "speculative-feedback-checkbox" },
	{ value: "haloAndHornsBiases", label: "Halo & Horns Biases", dataTestId: "halo-and-horns-biases-checkbox" },
	{ value: "conflictingRatings", label: "Conflicting Ratings", dataTestId: "conflicting-ratings-checkbox" },
	{
		value: "positiveNegativeImbalance",
		label: "Positive, Negative Imbalance",
		dataTestId: "positive-negative-imbalance-checkbox",
	},
];

const CoachingReviewsTab = ({ setIsUnsafe, setIsSafe }: CoachingReviewsTabProps) => {
	const { value: isFormAuthoringCoachFeatureEnabled } = useFeatureFlagQuery(
		"ff-temp-is-form-authoring-coach-enabled",
		false
	);

	const formRef = useRef<FormHandle<typeof formAuthoringCoachFormSchema>>(null);

	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	const isFormAuthoringCoachEnabled = data?.formAuthoringCoachEnabled ?? true;
	const isFormBuildingCoachEnabled = data?.formBuildingCoachEnabled ?? true;

	const handleFormAuthoringCoachToggle = async (val: boolean) => {
		await updateAiSettings({ formAuthoringCoachEnabled: val });
		toast.success(`Form Authoring Coach ${val === true ? "Enabled" : "Disabled"}`);
	};

	const handleFormBuildingCoachToggle = async (val: boolean) => {
		await updateAiSettings({ formBuildingCoachEnabled: val });
		toast.success(`Form Building Coach ${val === true ? "Enabled" : "Disabled"}`);
	};

	const handleSave = async ({
		value,
		resetForm,
	}: {
		value: z.infer<typeof formAuthoringCoachFormSchema>;
		resetForm: () => void;
	}) => {
		try {
			await updateAiSettings({ formAuthoringCoachSettings: value });
			toast.success("Your changes have been saved");
			resetForm();
			setIsSafe();
		} catch {
			toast.error("Unable to save settings");
		}
	};

	const defaultValues = useAiSettingsDefaults({
		schema: formAuthoringCoachFormSchema,
		data,
		extractDefaults: (allDefaults) => allDefaults.formAuthoringCoachSettings,
		extractData: (data) => data.formAuthoringCoachSettings,
	});

	if (isLoading) {
		return null;
	}

	return (
		<PYForm
			ref={formRef}
			id="coaching-review-settings-form"
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={formAuthoringCoachFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSave}
		>
			<UnsavedChangesTracker
				id="coaching-review-settings-unsaved-changes"
				setIsUnsafe={setIsUnsafe}
				setIsSafe={setIsSafe}
			/>

			{isFormAuthoringCoachFeatureEnabled && (
				<SectionLayout
					key="form-authoring-coach-section"
					title="Form Authoring Coach"
					description="Control whether members of your organization can receive improvement suggestions about the form they are authoring."
				>
					<SettingsToggle
						key="form-authoring-coach-toggle"
						id="enable-form-authoring-coach"
						label="Form Authoring Coach"
						description="Disabling this setting will remove PerformYard's ability to improve user authored forms."
						checked={isFormAuthoringCoachEnabled}
						onCheckedChange={handleFormAuthoringCoachToggle}
						dataTestId="form-authoring-coach-toggle"
					/>

					<CheckboxGroup
						key="form-authoring-coach-detections-checkboxes"
						title="Form Authoring Coach Detections"
						description="Control what the Form Authoring Coach evaluates in your forms, so it highlights only the issues that matter to you and your organization."
						options={formAuthoringDetectionCheckboxOptions}
					/>

					<GuidelinesField
						id="additionalGuidelines"
						dataTestId="additional-form-detection-guidelines"
						label="Additional Form Detection Guidelines"
						description={ADDITIONAL_GUIDELINES_DESCRIPTION}
					/>
				</SectionLayout>
			)}

			{isFormAuthoringCoachFeatureEnabled && (
				<>
					<Spacer key="form-building-coach-spacer-1" height={5} />
					<Divider key="form-building-coach-divider" className="m-0" />
					<Spacer key="form-building-coach-spacer-2" height={4} />
				</>
			)}

			<SectionLayout
				key="form-building-coach-section"
				title="Form Building Coach"
				description="Control whether admins of your organization can receive improvement suggestions about the form they are creating."
			>
				<SettingsToggle
					key="form-building-coach-toggle"
					id="enable-form-building-coach"
					label="Form Building Coach"
					description="Disabling this setting will remove PerformYard's ability to improve admin created forms."
					checked={isFormBuildingCoachEnabled}
					onCheckedChange={handleFormBuildingCoachToggle}
					dataTestId="form-building-coach-toggle"
				/>
			</SectionLayout>

			{isFormAuthoringCoachFeatureEnabled && (
				<SettingsFormFooter
					key="coaching-review-settings-footer"
					formId="coaching-review-settings-form"
					isPending={isPending}
				/>
			)}
		</PYForm>
	);
};

export default CoachingReviewsTab;
